import { useLocation, useResolvedPath } from "react-router";

import { type EntityTabItem } from "./EntityTabs";

/**
 * The value of the tab the URL is on: the first path segment after the page's
 * own route that names one of `tabs`. Deeper segments are ignored, so a tab
 * that owns paths of its own (a Files folder, a Metrics dashboard) stays
 * selected. A URL naming no tab, such as the page's bare route before its
 * index redirect, gives `fallback`, or the first tab.
 *
 * The page's route comes from the router, not from the caller: called from the
 * page's own component, `"."` resolves to the page's path, which is also what
 * the strip's tab links resolve against. The URL is the only source of truth:
 * tabs navigate through links, so there is no tab state to fall out of step
 * with the address bar.
 */
export function useActiveEntityTab<T extends string>(
  tabs: readonly EntityTabItem<T>[],
  fallback?: T,
): T {
  const { pathname } = useLocation();
  const page = useResolvedPath(".").pathname;
  const below = pathname.startsWith(page) ? pathname.slice(page.length) : "";
  const segment = below.split("/").find((part) => part !== "");
  const values = tabs.flatMap((tab) => (tab === "divider" ? [] : [tab.value]));
  const active = values.find((value) => value === segment);
  return active ?? fallback ?? values[0];
}
